import { inject, injectable } from "inversify";
import { type IService, Logger } from "@designboticteam/core";
import { MapStoreService } from "..";
import type { PmTilesSource, PmTilesSourceDefinition, PmTilesSourceId } from "./types";

@injectable()
class PmTilesService implements IService {
	public readonly serviceName = "🗺️ PmTilesService";
	private readonly logger = new Logger(`🌍 ${this.serviceName}`, {
		color: "#ffffff",
		backgroundColor: "#00aa00",
	});

	constructor(@inject(MapStoreService) private mapStore: MapStoreService) {
		this.logger.log("constructed");
	}

	/** */
	public initialize(): void {
		this.logger.log("initialized");
	}

	/** */
	public dispose(): void {
		this.logger.log("disposed");
	}

	/** Registers a layer. Runtime state (`isInitialized`, `isLoading`) is owned here, not by the caller. */
	public addLayer(source: PmTilesSourceDefinition): void {
		this.mapStore.setState((state) => {
			const pmTiles = new Map(state.sources.pmTiles);
			pmTiles.set(source.id, { ...source, isInitialized: false, isLoading: false });

			return {
				sources: {
					...state.sources,
					pmTiles: pmTiles,
				},
			};
		});
	}

	/** */
	public removeLayer(sourceId: PmTilesSourceId): void {
		this.mapStore.setState((state) => {
			if (state.sources.pmTiles.has(sourceId) === false) {
				return state;
			}

			const next = new Map(state.sources.pmTiles);
			next.delete(sourceId);

			return {
				sources: {
					...state.sources,
					pmTiles: next,
				},
			};
		});
	}

	/**
	 * Marks the source as initialized once its archive is fetched and layers are added.
	 */
	public setSourceInitialized(sourceId: PmTilesSourceId, isInitialized: boolean): void {
		this.mapStore.setState((state) => {
			const current = state.sources.pmTiles.get(sourceId);

			// Unknown id (base-style source) or no change - skip.
			if (current === undefined || current.isInitialized === isInitialized) {
				return state;
			}

			const pmTiles = new Map(state.sources.pmTiles);
			pmTiles.set(sourceId, { ...current, isInitialized });

			return {
				sources: {
					...state.sources,
					pmTiles,
				},
			};
		});
	}

	/**
	 * Reflects whether the source's tiles are currently in flight (e.g. while panning/zooming).
	 */
	public setSourceLoading(sourceId: PmTilesSourceId, isLoading: boolean): void {
		this.mapStore.setState((state) => {
			const current = state.sources.pmTiles.get(sourceId);

			// Unknown id (base-style source) or no change - skip.
			if (current === undefined || current.isLoading === isLoading) {
				return state;
			}

			const pmTiles = new Map(state.sources.pmTiles);
			pmTiles.set(sourceId, { ...current, isLoading });

			return {
				sources: {
					...state.sources,
					pmTiles,
				},
			};
		});
	}

	/** */
	public updateSourceDisplaySettings(sourceId: PmTilesSourceId, patch: Partial<PmTilesSource["displaySettings"]>): void {
		this.mapStore.setState((state) => {
			const current = state.sources.pmTiles.get(sourceId);

			if (current === undefined) {
				return state;
			}

			const pmTiles = new Map(state.sources.pmTiles);

			pmTiles.set(sourceId, {
				...current,
				displaySettings: {
					...current.displaySettings,
					...patch,
				},
			});

			return {
				sources: {
					...state.sources,
					pmTiles,
				},
			};
		});
	}
}

export { PmTilesService };
